import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  atamaListesi,
  atamaOnayla,
  atamaReddet,
  atamaSonlandir,
  onayBekleyenAtamalar,
  type AtamaDurumu,
  type AtamaOzeti,
  type AtamaSorgusu,
} from '../api/atama';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { Diyalog, GerekceDiyalogu } from '../bilesenler/Diyalog';
import { Sayfalama, Tablo, type Sutun } from '../bilesenler/Tablo';
import { Dugme, HataKutusu, Metin, Sekme } from '../bilesenler/temel';
import { bugun, tarihMetni } from '../bicim/tarih';
import { sayfalamaKullan } from '../kancalar/sayfalama';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';

/**
 * ATAMALAR.
 *
 * ATAMA SİSTEMİN OMURGASIDIR: yetki pozisyondan, kapsam birimden gelir ve
 * ikisi de atama üzerinden çözülür. Puantaj, izin, görev — hepsi `atamaId`
 * ile kayıtlanır. Kişi birim değiştirdiğinde geçmiş kayıtlar ESKİ birimde
 * kalır; personel kimliğine bağlansaydı tarihsel raporlar yeni birime kayardı.
 *
 * YENİ ATAMA FORMU YOK ve sebebi somut: `POST /atama` `pozisyonId` ve
 * `atamaTipiId` istiyor ama İKİSİNİN DE KATALOG UCU BULUNMUYOR. `gun_tipi`
 * ve `izin_tipi` ile aynı boşluk (`IS_LISTESI.md` §3.16). Sabit yazmak
 * güvensiz: id'ler `serial` ve kurulumdan kuruluma değişir.
 *
 * YAPILABİLENLER: listeleme, ayrıntı, onay, red, sonlandırma. Onay akışı
 * zaten sunumun asıl gösterdiği şey — görevler ayrılığı.
 */

/*
 * DURUMLAR ŞEMADAN: taslak → onay_bekliyor → aktif → sonlandi, ya da iptal.
 *
 * İlk yazımda `talep` ve `reddedildi` varsayılmıştı; `tsc` yakaladı. Akış
 * gerçekte ikiye ayrılıyor: TASLAK henüz gönderilmemiş bir hazırlık,
 * ONAY_BEKLIYOR ise karar kuyruğunda. Reddedilen atama `iptal` oluyor —
 * ayrı bir "reddedildi" durumu yok çünkü sonuç aynı: atama yürürlüğe
 * girmiyor ve kayıt gerekçesiyle duruyor.
 */
const DURUM_ETIKETI: Record<AtamaDurumu, string> = {
  taslak: 'Taslak',
  onay_bekliyor: 'Onay bekliyor',
  aktif: 'Aktif',
  sonlandi: 'Sonlandı',
  iptal: 'İptal',
};

const DURUM_BICIMI: Record<AtamaDurumu, string> = {
  taslak: 'bg-slate-100 text-slate-600 ring-slate-200',
  onay_bekliyor: 'bg-amber-50 text-amber-800 ring-amber-200',
  aktif: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  sonlandi: 'bg-slate-100 text-slate-600 ring-slate-200',
  iptal: 'bg-red-50 text-red-700 ring-red-200',
};

const DURUMLAR: AtamaDurumu[] = ['taslak', 'onay_bekliyor', 'aktif', 'sonlandi', 'iptal'];

function DurumRozeti({ durum }: { durum: AtamaDurumu }) {
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${DURUM_BICIMI[durum]}`}
    >
      {DURUM_ETIKETI[durum]}
    </span>
  );
}

type Eylem =
  | { tur: 'onay'; a: AtamaOzeti }
  | { tur: 'red'; a: AtamaOzeti }
  | { tur: 'sonlandir'; a: AtamaOzeti };

export function Atamalar() {
  const yetkilerHazir = yetkilerHazirKullan();

  const gorebilir = yetkiKullan('PERSONEL_GORUNTULE', 'ATAMA_YAP', 'ATAMA_ONAYLA');
  const onaylayabilir = yetkiKullan('ATAMA_ONAYLA');

  const [sekmeSecimi, sekmeSecimiYaz] = useState<'liste' | 'onay' | null>(null);
  const sekme = sekmeSecimi ?? (gorebilir ? 'liste' : 'onay');

  if (!yetkilerHazir) {
    return <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>;
  }

  if (!gorebilir && !onaylayabilir) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
        Bu ekranı görüntüleme yetkiniz yok.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Atamalar</h1>
        <p className="mt-1 text-sm text-slate-600">
          Yetki pozisyondan, kapsam birimden gelir; ikisi de atama üzerinden çözülür. Atama
          SİLİNMEZ, sonlandırılır.
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        {gorebilir && (
          <Sekme etkin={sekme === 'liste'} tikla={() => sekmeSecimiYaz('liste')} etiket="Tümü" />
        )}
        {onaylayabilir && (
          <Sekme
            etkin={sekme === 'onay'}
            tikla={() => sekmeSecimiYaz('onay')}
            etiket="Onay bekleyenler"
          />
        )}
      </div>

      {sekme === 'liste' && gorebilir ? (
        <AtamaListesi onaylayabilir={onaylayabilir} />
      ) : (
        <OnayKuyrugu />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function sutunlariUret(
  onaylayabilir: boolean,
  eylemYaz: (e: Eylem) => void,
  ayrintiAc: (a: AtamaOzeti) => void,
): Sutun<AtamaOzeti>[] {
  return [
    {
      baslik: 'Personel',
      hucre: (a) => (
        <span>
          {/*
            ADA TIKLAYINCA PUANTAJ ÇİZELGESİ. Atama, puantajın anahtarı;
            "bu atamanın kayıtları nasıl" sorusu bir tık uzakta olmalı.
          */}
          <Link to={`/puantaj/${a.atamaId}`} className="block text-kurum-700 hover:underline">
            {a.personelAdSoyad}
          </Link>
          <span className="block font-mono text-xs text-slate-500">{a.personelNo}</span>
        </span>
      ),
    },
    {
      baslik: 'Birim / Pozisyon',
      hucre: (a) => (
        <span>
          <span className="block text-slate-900">{a.birimAd}</span>
          <span className="block text-xs text-slate-500">
            {a.pozisyonAd}
            {a.birincilMi && ' · birincil'}
            {a.atamaTipiKod !== 'ASLI' && ` · ${a.atamaTipiKod.toLocaleLowerCase('tr-TR')}`}
          </span>
        </span>
      ),
    },
    {
      baslik: 'Süre',
      hucre: (a) => (
        <span className="whitespace-nowrap text-xs">
          <span className="block">{tarihMetni(a.baslangicTarihi)}</span>
          <span className="block text-slate-500">
            {a.bitisTarihi === null ? 'süresiz' : tarihMetni(a.bitisTarihi)}
          </span>
        </span>
      ),
      daralt: true,
    },
    {
      baslik: 'Yetki',
      /*
       * POZİSYONUN TAŞIDIĞI YETKİLER özet olarak gösterilir: bir atamayı
       * onaylayan kişi, o kişinin ne yapabileceğini bilmeden karar vermemeli.
       */
      hucre: (a) => (
        <span className="flex flex-wrap gap-1 text-xs">
          {a.yoneticiMi && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">yönetici</span>
          )}
          {a.onayYetkisiVarMi && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">onay</span>
          )}
          {a.puantajGirebilirMi && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">puantaj</span>
          )}
        </span>
      ),
      daralt: true,
    },
    { baslik: 'Durum', hucre: (a) => <DurumRozeti durum={a.durum} />, daralt: true },
    {
      baslik: '',
      hucre: (a) => (
        <span className="flex justify-end gap-1 whitespace-nowrap">
          <button
            type="button"
            onClick={() => ayrintiAc(a)}
            className="rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
          >
            Ayrıntı
          </button>
          {onaylayabilir && a.durum === 'onay_bekliyor' && (
            <>
              <button
                type="button"
                onClick={() => eylemYaz({ tur: 'onay', a })}
                className="rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
              >
                Onayla
              </button>
              <button
                type="button"
                onClick={() => eylemYaz({ tur: 'red', a })}
                className="rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
              >
                Reddet
              </button>
            </>
          )}
          {onaylayabilir && a.durum === 'aktif' && (
            <button
              type="button"
              onClick={() => eylemYaz({ tur: 'sonlandir', a })}
              className="rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Sonlandır
            </button>
          )}
        </span>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];
}

/* ------------------------------------------------------------------ */

interface AtamaSuzgeci {
  birimId?: Kimlik;
  durum?: AtamaDurumu;
  gecerliMi?: boolean;
}

function AtamaListesi({ onaylayabilir }: { onaylayabilir: boolean }) {
  const { suzgec, suzgeciYaz, sayfa, sayfayaGit, sayfaBoyutu } = sayfalamaKullan<AtamaSuzgeci>({
    gecerliMi: true,
  });

  const [eylem, eylemYaz] = useState<Eylem | null>(null);
  const [ayrinti, ayrintiYaz] = useState<AtamaOzeti | null>(null);

  const sorgu: AtamaSorgusu = { ...suzgec, sayfa, sayfaBoyutu };

  const liste = useQuery({
    queryKey: ['atama', 'liste', sorgu],
    queryFn: () => atamaListesi(sorgu),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <BirimSecici
            deger={suzgec.birimId}
            degisti={(y) => suzgeciYaz((o) => ({ ...o, birimId: y }))}
            bosSecenek="Kapsamımdaki tüm birimler"
          />
        </div>

        <label className="text-sm text-slate-700">
          <span className="mb-1 block font-medium">Durum</span>
          <select
            value={suzgec.durum ?? ''}
            onChange={(olay) =>
              suzgeciYaz((o) => ({
                ...o,
                durum: olay.target.value === '' ? undefined : (olay.target.value as AtamaDurumu),
              }))
            }
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
          >
            <option value="">Tümü</option>
            {DURUMLAR.map((d) => (
              <option key={d} value={d}>
                {DURUM_ETIKETI[d]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={suzgec.gecerliMi === true}
            onChange={(olay) =>
              suzgeciYaz((o) => ({ ...o, gecerliMi: olay.target.checked ? true : undefined }))
            }
            className="rounded border-slate-300"
          />
          {/* `gecerlilik @> CURRENT_DATE` — bugün yürürlükte olanlar. */}
          Yalnız bugün geçerli
        </label>
      </div>

      <HataKutusu hata={liste.error} />

      <Tablo
        sutunlar={sutunlariUret(onaylayabilir, eylemYaz, ayrintiYaz)}
        satirlar={liste.data?.kayitlar ?? []}
        anahtar={(a) => a.atamaId}
        bekliyor={liste.isPending}
        bosMetin="Bu süzgeçle atama bulunamadı."
      />

      <Sayfalama
        sayfa={sayfa}
        sayfaBoyutu={sayfaBoyutu}
        toplam={liste.data?.toplam ?? 0}
        sayfayaGit={sayfayaGit}
        bekliyor={liste.isFetching}
      />

      {eylem !== null && <AtamaKarari eylem={eylem} kapat={() => eylemYaz(null)} />}
      {ayrinti !== null && <AtamaAyrintisi atama={ayrinti} kapat={() => ayrintiYaz(null)} />}
    </div>
  );
}

function OnayKuyrugu() {
  const [eylem, eylemYaz] = useState<Eylem | null>(null);
  const [ayrinti, ayrintiYaz] = useState<AtamaOzeti | null>(null);

  const liste = useQuery({
    queryKey: ['atama', 'onay-bekleyenler'],
    queryFn: () => onayBekleyenAtamalar(),
  });

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        GÖREVLER AYRILIĞI: atamayı talep eden onaylayamaz. Kendi atamanızı talep etmeniz de sunucu
        tarafında engellidir.
      </p>

      <HataKutusu hata={liste.error} />

      <Tablo
        sutunlar={sutunlariUret(true, eylemYaz, ayrintiYaz)}
        satirlar={liste.data ?? []}
        anahtar={(a) => a.atamaId}
        bekliyor={liste.isPending}
        bosMetin="Onay bekleyen atama yok."
      />

      {eylem !== null && <AtamaKarari eylem={eylem} kapat={() => eylemYaz(null)} />}
      {ayrinti !== null && <AtamaAyrintisi atama={ayrinti} kapat={() => ayrintiYaz(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function AtamaKarari({ eylem, kapat }: { eylem: Eylem; kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  // Sonlandırma için bitiş tarihi ZORUNLU; bugün ön dolu ama geriye dönük
  // de verilebilir (görevden ayrılma tarihi geçmiş olabilir).
  const [bitisTarihi, bitisTarihiYaz] = useState(() => bugun());

  const karar = useMutation({
    mutationFn: (metin: string) => {
      const id = eylem.a.atamaId;
      switch (eylem.tur) {
        case 'onay':
          return atamaOnayla(id, metin === '' ? undefined : metin);
        case 'red':
          return atamaReddet(id, metin);
        case 'sonlandir':
          return atamaSonlandir(id, bitisTarihi, metin === '' ? undefined : metin);
      }
    },
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['atama'] });
      kapat();
    },
  });

  const baslik =
    eylem.tur === 'onay'
      ? 'Atamayı onayla'
      : eylem.tur === 'red'
        ? 'Atamayı reddet'
        : 'Atamayı sonlandır';

  return (
    <GerekceDiyalogu
      baslik={baslik}
      aciklama={`${eylem.a.personelAdSoyad} · ${eylem.a.birimAd} · ${eylem.a.pozisyonAd}`}
      etiket={eylem.tur === 'red' ? 'Red gerekçesi' : 'Gerekçe'}
      ipucu={
        eylem.tur === 'red'
          ? 'Zorunlu — talep eden neden reddedildiğini bilmeli.'
          : 'İsteğe bağlı — denetim izine yazılır.'
      }
      onayEtiketi={eylem.tur === 'onay' ? 'Onayla' : eylem.tur === 'red' ? 'Reddet' : 'Sonlandır'}
      onayTuru={eylem.tur === 'onay' ? 'birincil' : 'tehlike'}
      zorunluMu={eylem.tur === 'red'}
      enCokKarakter={500}
      bekliyor={karar.isPending}
      hata={karar.error}
      kapat={kapat}
      onayla={(metin) => karar.mutate(metin)}
      ustBilgi={
        eylem.tur === 'sonlandir' ? (
          <div className="space-y-2">
            <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              Atama SİLİNMEZ, bitiş tarihi yazılır. O tarihe kadarki puantaj ve izin kayıtları bu
              atamaya bağlı kalır.
            </p>
            <Metin
              etiket="Bitiş tarihi"
              tur="date"
              deger={bitisTarihi}
              degisti={bitisTarihiYaz}
              ipucu="Geriye dönük verilebilir — görevden ayrılma tarihi geçmiş olabilir."
            />
          </div>
        ) : undefined
      }
    />
  );
}

/* ------------------------------------------------------------------ */

function AtamaAyrintisi({ atama, kapat }: { atama: AtamaOzeti; kapat: () => void }) {
  return (
    <Diyalog
      baslik={`${atama.personelAdSoyad} — atama`}
      aciklama={`${atama.birimAd} · ${atama.pozisyonAd}`}
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <DurumRozeti durum={atama.durum} />
            {atama.birincilMi && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                Birincil atama
              </span>
            )}
            {atama.vekaletVerilenAtamaId !== null && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                Vekâlet
              </span>
            )}
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <Kutu etiket="Başlangıç" deger={tarihMetni(atama.baslangicTarihi)} />
            <Kutu
              etiket="Bitiş"
              deger={atama.bitisTarihi === null ? 'Süresiz' : tarihMetni(atama.bitisTarihi)}
            />
            <Kutu etiket="Atama tipi" deger={atama.atamaTipiKod} />
            <Kutu etiket="Kademe" deger={String(atama.kademeSeviye)} />
            {/*
              DAYANAK ZORUNLU ALAN: her atama bir belgeye dayanır. Dayanaksız
              atama denetimde "bu kişi buraya neden atandı" sorusunu cevapsız
              bırakır.
            */}
            <Kutu
              etiket="Dayanak"
              deger={`${atama.dayanakTipi}${atama.dayanakNo !== null ? ` · ${atama.dayanakNo}` : ''}`}
            />
            <Kutu
              etiket="Onay"
              deger={atama.onayTarihi === null ? '—' : tarihMetni(atama.onayTarihi)}
            />
          </dl>

          <section className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Bu pozisyonun taşıdığı yetkiler
            </h3>
            <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
              <li>Yönetici: {atama.yoneticiMi ? 'evet' : 'hayır'}</li>
              <li>Onay yetkisi: {atama.onayYetkisiVarMi ? 'var' : 'yok'}</li>
              <li>Puantaj girebilir: {atama.puantajGirebilirMi ? 'evet' : 'hayır'}</li>
            </ul>
          </section>

          <div className="flex justify-end gap-2">
            <Link
              to={`/puantaj/${atama.atamaId}`}
              className="rounded-md border border-kurum-200 bg-kurum-50 px-3 py-1.5 text-sm font-medium text-kurum-800 hover:bg-kurum-100"
            >
              Puantaj çizelgesi
            </Link>
            <Dugme tur="ikincil" tikla={kapat} cocuk="Kapat" />
          </div>
        </div>
      }
    />
  );
}

function Kutu({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <dt className="text-xs text-slate-500">{etiket}</dt>
      <dd className="mt-0.5 font-medium text-slate-900">{deger}</dd>
    </div>
  );
}
